import React from 'react'
import * as Mapbox from "react-map-gl/mapbox";
import {Feature, FeatureCollection, Geometry} from "geojson";
import {SitumFeatureProperties} from "../../../types/Situm";
import {getModifiedSitumFeatures, getSitumBuildingLayers} from "../../../services/situm";
import {feature, featureCollection} from "../../../services/geometry";
import ngage from "../../../services/ngage";
import {useQuery} from "react-query";
import axios from "axios";
import FloorZoneSpaceLayer from "../Layers/FloorZoneSpaceLayer";
import {Building} from "../../../types/Building";
import {MapLayer} from "../../../types/MapLayers";

// Helpers ----

function getFloorSpacesFeatureCollection(building: Building) {
  const floorSpacesFeatures: Feature[] = [];

  building?.floors.forEach(floor => {
    floor.spaces.forEach(space => {
      floorSpacesFeatures.push(
        feature(space.uuid, {
          id: space.id,
          uuid: space.uuid,
          name: space.zone.name,
          type: 'floor-zone-space',
          floor_id: floor.id,
        }, space.zone.area)
      )
    });
  });

  return featureCollection(floorSpacesFeatures);
}

function getBeforeId(enabledLayers: MapLayer[]) {
  const priority = [
    MapLayer.EXHIBIT,
    MapLayer.FACILITY,
    MapLayer.TRAIL,
    MapLayer.BEACON,
  ];
  return priority.find(layer => enabledLayers.includes(layer));
}

// Types ----

type BuildingSourceProps = {
  buildingId: number
  floorId: number
  enabledLayers: MapLayer[]
}

// Component ----

function SitumBuildingSource({buildingId, floorId, enabledLayers}: BuildingSourceProps) {
  // Get building data
  const {data: building} = useQuery(['building', buildingId], () => ngage.buildings.get(buildingId));

  // Get Situm floor
  const situmFloorId = Number(building?.floors.find(floor => floor.id === floorId)?.reference ?? 0);

  // Get Situm Feature Collection and Styles
  const featureCollectionUrl: string = building?.additional_data?.customFields?.find((field: any) => field.key === 'geojson_map_url')?.value;
  const featureStylesUrl: string = building?.additional_data?.customFields?.find((field: any) => field.key === 'geojson_theme_url')?.value;
  const situmRequestOptions = { headers: {accept: 'application/json'}};
  const {data: situmFeatureCollection} = useQuery(['situmFeatureCollection', buildingId], () => axios.get(featureCollectionUrl, situmRequestOptions), { enabled: !!featureCollectionUrl});
  const {data: featureStyles} = useQuery(['situmFeatureStyles', buildingId], () => axios.get(featureStylesUrl, situmRequestOptions), { enabled: !!featureStylesUrl});

  // Create feature collection
  const situmFeatures = situmFeatureCollection ? situmFeatureCollection.data.features : [];
  const modifiedFeatures = getModifiedSitumFeatures(situmFeatures);
  const situmFeatureCollectionModified = featureCollection(modifiedFeatures) as FeatureCollection<Geometry, SitumFeatureProperties>;

  // Create layers
  const layers = (situmFeatureCollectionModified && featureStyles?.data) ? getSitumBuildingLayers(buildingId, situmFeatureCollectionModified, featureStyles.data) : [];

  // Create floor spaces feature collection
  const floorSpacesFeatureCollection = building ? getFloorSpacesFeatureCollection(building) : featureCollection([]);

  // If no layers are available, return null
  if (!layers.length) return null;

  // Else, render the Mapbox layers
  return (
    <>
      <Mapbox.Source type="geojson" id={`situm-building-${buildingId}`} data={situmFeatureCollectionModified}>
        {layers.map((layer) => (
          <Mapbox.Layer
            {...layer}
            key={layer.id}
            beforeId={getBeforeId(enabledLayers)}
            layout={{ visibility: enabledLayers.includes(MapLayer.BUILDING) ? 'visible' : 'none'}}
            filter={["all", layer.filter, ["has", "floor_id"], ["==", "floor_id", situmFloorId]]}
          />
        ))}
      </Mapbox.Source>

      <Mapbox.Source type="geojson" id={`situm-building-floor-spaces`} data={floorSpacesFeatureCollection}>
        <FloorZoneSpaceLayer
          enabledLayers={enabledLayers}
          floorId={floorId}
          beforeId={getBeforeId(enabledLayers)}
        />
      </Mapbox.Source>
    </>
  );
}

export {SitumBuildingSource};